import { isHabitFulfilled, isScheduledDate, type Habit } from '@cleartask/shared';

export interface StreakBarProps {
  habit: Habit;
  /** Datas da faixa, em ordem cronologica. */
  dates: string[];
  /** Valor registrado por data. Ausente = dia sem registro. */
  values: Map<string, number>;
}

/**
 * A faixa `● ● ○ ● ● ● ·` do wireframe (spec 9.5).
 *
 * Os tres estados sao os mesmos que a regra de streak enxerga, e vem das mesmas
 * funcoes que o servidor usa para contar:
 *
 *   ●  previsto e cumprido
 *   ○  previsto e vazio  — este e o unico que quebra a sequencia
 *   ·  nao previsto      — nao conta e nao quebra
 *
 * Sem o terceiro simbolo, um habito de segunda/quarta/sexta apareceria com
 * quatro buracos por semana e o streak intacto ao lado, o que leria como bug.
 */
export function StreakBar({ habit, dates, values }: StreakBarProps) {
  const cumpridos = dates.filter((date) => {
    const valor = values.get(date);
    return valor !== undefined && isHabitFulfilled(habit, valor);
  }).length;

  const previstos = dates.filter((date) => isScheduledDate(habit.daysMask, date)).length;

  return (
    <div
      className="flex items-center gap-1.5"
      // Uma unica etiqueta em vez de sete itens de lista: o leitor de tela
      // anuncia o resumo que interessa, nao "bolinha, bolinha, bolinha".
      role="img"
      aria-label={`${cumpridos} de ${previstos} dias previstos nos últimos ${dates.length} dias`}
    >
      {dates.map((date) => {
        const previsto = isScheduledDate(habit.daysMask, date);
        const valor = values.get(date);
        const cumprido = valor !== undefined && isHabitFulfilled(habit, valor);

        if (!previsto) {
          return (
            <span
              key={date}
              aria-hidden="true"
              className="h-1 w-1 rounded-full bg-text-mut/40"
            />
          );
        }

        return (
          <span
            key={date}
            aria-hidden="true"
            className="h-2.5 w-2.5 rounded-full border"
            style={{
              borderColor: habit.color,
              backgroundColor: cumprido ? habit.color : 'transparent',
            }}
          />
        );
      })}
    </div>
  );
}
